import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentIntervalClient, PaymentIntervalDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new PaymentIntervalClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const usePaymentIntervals = () => {
    return useQuery({
        queryKey: ['paymentIntervals'],
        queryFn: async () => {
            const response = await apiClient.searchPaymentIntervals({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const usePaymentIntervalSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchPaymentIntervals(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const usePaymentIntervalById = (id: string) => {
    return useQuery({
        queryKey: ['paymentInterval', id],
        queryFn: async () => {
            const response = await apiClient.getPaymentIntervalById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreatePaymentInterval = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: PaymentIntervalDTO) => {
            return await apiClient.createPaymentInterval(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentIntervals'] });
        },
    });
};

// Update mutation
export const useUpdatePaymentInterval = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: PaymentIntervalDTO }) => {
            return await apiClient.updatePaymentInterval(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentIntervals'] });
        },
    });
};

// Delete mutation
export const useDeletePaymentInterval = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deletePaymentInterval(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentIntervals'] });
        },
    });
};